// src/pages/Tour/DealerVisit.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dealerVisitService from '../../services/dealerVisit.service';
import tourService from '../../services/tour.service';
import api from '../../services/api';
import Toast from '../../components/Toast/Toast';
import './TourPages.css';

const formatTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const DealerVisit = () => {
  const navigate = useNavigate();

  const [todayTour, setTodayTour]     = useState(null);
  const [tourLoading, setTourLoading] = useState(true);
  const [dealers, setDealers]         = useState([]);
  const [subDealers, setSubDealers]   = useState([]);
  const [dealerSearch, setDealerSearch] = useState('');
  const [selectedDealer, setSelectedDealer]       = useState(null);
  const [selectedSubDealer, setSelectedSubDealer] = useState(null);
  const [visitType, setVisitType]     = useState('DEALER');   // 'DEALER' | 'SUB_DEALER'
  const [todayVisits, setTodayVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [detecting, setDetecting]     = useState(false);
  const [toast, setToast]             = useState(null);
  const [errors, setErrors]           = useState({});

  const [formData, setFormData] = useState({
    dealerId: '',
    subDealerId: '',
    visitLatitude: '',
    visitLongitude: '',
    remark: '',
  });

  useEffect(() => {
    tourService.getTodayTour()
      .then(r => { if (r.success && r.data) setTodayTour(r.data); })
      .catch(() => {})
      .finally(() => setTourLoading(false));
  }, []);

  // Load dealers when searching (parent dropdown)
  useEffect(() => {
    dealerVisitService.getDealers(dealerSearch)
      .then(r => { if (r.success && r.data) setDealers(r.data); })
      .catch(() => {});
  }, [dealerSearch]);

  // ✅ Load sub-dealers when a parent dealer is selected (only if visitType is SUB_DEALER)
  useEffect(() => {
    setSubDealers([]);
    setSelectedSubDealer(null);
    setFormData(prev => ({ ...prev, subDealerId: '' }));

    if (visitType !== 'SUB_DEALER' || !formData.dealerId) return;

    api.get('/dealer-visits/sub-dealers')
      .then(res => {
        const list = res.data?.data || [];
        // Filter to only the selected parent dealer's sub-dealers
        const filtered = list.filter(sd => sd.dealerId === parseInt(formData.dealerId));
        setSubDealers(filtered);
      })
      .catch(() => setSubDealers([]));
  }, [visitType, formData.dealerId]);

  useEffect(() => { fetchTodayVisits(); }, []);

  const fetchTodayVisits = async () => {
    setVisitsLoading(true);
    try {
      const r = await dealerVisitService.getTodayVisits();
      if (r.success && r.data) setTodayVisits(r.data);
    } catch (_) {}
    finally { setVisitsLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleVisitTypeChange = (type) => {
    setVisitType(type);
    setSelectedSubDealer(null);
    setFormData(prev => ({ ...prev, subDealerId: '' }));
    setErrors({});
  };

  const handleDealerChange = (e) => {
    const dealerId = e.target.value;
    setFormData(prev => ({ ...prev, dealerId, subDealerId: '' }));
    const dealer = dealers.find(d => String(d.id) === String(dealerId));
    setSelectedDealer(dealer || null);
    setSelectedSubDealer(null);
    setErrors(prev => ({ ...prev, dealerId: '', subDealerId: '', visitLatitude: '' }));
  };

  const handleSubDealerChange = (e) => {
    const subDealerId = e.target.value;
    setFormData(prev => ({ ...prev, subDealerId }));
    const sd = subDealers.find(s => String(s.id) === String(subDealerId));
    setSelectedSubDealer(sd || null);
    setErrors(prev => ({ ...prev, subDealerId: '', visitLatitude: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

const finish = (position) => {
  if (settled) return;
  settled = true;
  navigator.geolocation.clearWatch(watchId);

  const { latitude, longitude, accuracy } = position.coords;
  setFormData(prev => ({
    ...prev,
    visitLatitude: latitude.toFixed(8),      // was: latitude
    visitLongitude: longitude.toFixed(8),    // was: longitude
    visitLocationAccuracy: accuracy,         // was: locationAccuracy
  }));

  setDetecting(false);                       // see note below
  setToast({ message: '📍 Location detected!', type: 'success' });
};

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const newErrors = {};
    if (!formData.dealerId) newErrors.dealerId = 'Please select a dealer';
    if (visitType === 'SUB_DEALER' && !formData.subDealerId) {
      newErrors.subDealerId = 'Please select a sub-dealer';
    }
    if (!formData.visitLatitude || !formData.visitLongitude) {
      newErrors.visitLatitude = 'Your location is required to log a visit';
    }

    // 1000m geo-fence against the active target
    const target = visitType === 'SUB_DEALER' ? selectedSubDealer : selectedDealer;
    const targetName = target?.subDealerShopName || target?.dealerShopName;
    if (target?.latitude && target?.longitude && formData.visitLatitude && formData.visitLongitude) {
      const distance = haversineDistance(
        parseFloat(formData.visitLatitude),
        parseFloat(formData.visitLongitude),
        parseFloat(target.latitude),
        parseFloat(target.longitude)
      );
      if (distance > 1000) {
        newErrors.visitLatitude =
          `You are ${Math.round(distance)}m away from ${targetName}. Must be within 1000m.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        dealerId:       parseInt(formData.dealerId),
        subDealerId:    visitType === 'SUB_DEALER' && formData.subDealerId
                        ? parseInt(formData.subDealerId)
                        : undefined,
        visitLatitude:  formData.visitLatitude  || undefined,
        visitLongitude: formData.visitLongitude || undefined,
        remark:         formData.remark         || undefined,
      };

      const response = await dealerVisitService.createVisit(payload);
      if (response.success) {
        setToast({ message: 'Visit logged successfully!', type: 'success' });
        setFormData({ dealerId: '', subDealerId: '', visitLatitude: '', visitLongitude: '', remark: '' });
        setSelectedDealer(null);
        setSelectedSubDealer(null);
        setSubDealers([]);
        setDealerSearch('');
        fetchTodayVisits();
      }
    } catch (error) {
      setToast({
        message: error.response?.data?.message || 'Failed to log visit',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (tourLoading) return (
    <div className="tour-page-container">
      <div className="tour-loading">Checking tour status...</div>
    </div>
  );

  // The locked/non-working states (no tour, completed, leave) — kept identical to your original
  if (!todayTour || todayTour.status === 'COMPLETED' || todayTour.tourType !== 'WORKING') {
    return (
      <div className="tour-page-container" style={{ maxWidth: 900 }}>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <nav className="tour-breadcrumb">
          <a href="/dashboard/overview">Dashboard</a><span>›</span>
          <span>Tour</span><span>›</span><span>Dealer Visit</span>
        </nav>
        <div className="tour-header">
          <h4 className="tour-title">Dealer Visit</h4>
        </div>
        <div className="tour-locked-card">
          <div className="tour-locked-icon">
            {!todayTour ? '⚠️' : todayTour.status === 'COMPLETED' ? '🔒' : '📋'}
          </div>
          <h5 className="tour-locked-title">
            {!todayTour ? 'No Tour Started Today' :
             todayTour.status === 'COMPLETED' ? 'Tour Completed' :
             todayTour.tourType === 'LEAVE' ? 'Leave Day' : 'Campaigning Day'}
          </h5>
          <p className="tour-locked-text">
            {!todayTour ? 'You must start your tour before logging dealer visits.' :
             todayTour.status === 'COMPLETED' ? 'Your tour is completed.' :
             'Dealer visits cannot be logged today.'}
          </p>
          {!todayTour && (
            <button className="btn-tour-primary" onClick={() => navigate('/tour/start-km')}>
              Go to Start KM
            </button>
          )}
        </div>
      </div>
    );
  }

  const activeTarget = visitType === 'SUB_DEALER' ? selectedSubDealer : selectedDealer;
  const activeTargetName = activeTarget?.subDealerShopName || activeTarget?.dealerShopName;

  return (
    <div className="tour-page-container" style={{ maxWidth: 900 }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav className="tour-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a><span>›</span>
        <span>Tour</span><span>›</span><span>Dealer Visit</span>
      </nav>

      <div className="tour-header">
        <h4 className="tour-title">Dealer Visit</h4>
        <p className="tour-subtitle">Log dealer or sub-dealer visits for today's tour</p>
      </div>

      <div className="tour-status-banner started" style={{ marginBottom: 20 }}>
        <span className="status-dot green" />
        Tour active — Start KM: <strong>{todayTour.startKm}</strong>
        &nbsp;|&nbsp;Visits today: <strong>{todayVisits.length}</strong>
      </div>

      <div className="tour-form-card">
        <p className="form-section-title">Log New Visit</p>

        {/* ✅ Visit type toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => handleVisitTypeChange('DEALER')}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8,
              border: visitType === 'DEALER' ? '2px solid #4a8c2a' : '1px solid #e0e0e0',
              background: visitType === 'DEALER' ? '#e8f5e9' : '#fff',
              color: visitType === 'DEALER' ? '#2e7d32' : '#495057',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            🏪 Dealer Visit
          </button>
          <button
            type="button"
            onClick={() => handleVisitTypeChange('SUB_DEALER')}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8,
              border: visitType === 'SUB_DEALER' ? '2px solid #4a8c2a' : '1px solid #e0e0e0',
              background: visitType === 'SUB_DEALER' ? '#e8f5e9' : '#fff',
              color: visitType === 'SUB_DEALER' ? '#2e7d32' : '#495057',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            🏬 Sub-Dealer Visit
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Dealer */}
          <div className="tour-form-row">
            <div className="tour-form-group">
              <label className="tour-label">Search Dealer</label>
              <input type="text" className="tour-input"
                placeholder="Type to search dealers..."
                value={dealerSearch} onChange={e => setDealerSearch(e.target.value)} />
            </div>
            <div className="tour-form-group">
              <label className="tour-label">Select Dealer <span className="required">*</span></label>
              <select name="dealerId"
                className={`tour-select ${errors.dealerId ? 'error' : ''}`}
                value={formData.dealerId} onChange={handleDealerChange}>
                <option value="">-- Select Dealer --</option>
                {dealers.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.dealerShopName} — {d.ownerName}
                  </option>
                ))}
              </select>
              {errors.dealerId && <span className="tour-error-message">{errors.dealerId}</span>}
            </div>
          </div>

          {/* ✅ Sub-Dealer (only shown when toggled) */}
          {visitType === 'SUB_DEALER' && (
            <div className="tour-form-row">
              <div className="tour-form-group">
                <label className="tour-label">
                  Select Sub-Dealer <span className="required">*</span>
                </label>
                <select name="subDealerId"
                  className={`tour-select ${errors.subDealerId ? 'error' : ''}`}
                  value={formData.subDealerId} onChange={handleSubDealerChange}
                  disabled={!formData.dealerId}>
                  <option value="">
                    {!formData.dealerId
                      ? '-- Select Dealer first --'
                      : subDealers.length === 0
                        ? '-- No sub-dealers under this dealer --'
                        : '-- Select Sub-Dealer --'}
                  </option>
                  {subDealers.map(sd => (
                    <option key={sd.id} value={sd.id}>
                      {sd.subDealerShopName} — {sd.ownerName}
                    </option>
                  ))}
                </select>
                {errors.subDealerId && <span className="tour-error-message">{errors.subDealerId}</span>}
              </div>
            </div>
          )}

          {/* Target location info */}
          {activeTarget && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 12,
              background: activeTarget.latitude ? '#e8f5e9' : '#fff8e1',
              border: `1px solid ${activeTarget.latitude ? '#a5d6a7' : '#ffe082'}`,
              fontSize: 13,
            }}>
              {activeTarget.latitude ? (
                <span style={{ color: '#2e7d32' }}>
                  📍 {activeTargetName} location saved — you must be within <strong>1000m</strong>
                </span>
              ) : (
                <span style={{ color: '#f57f17' }}>
                  ⚠️ {activeTargetName} has no saved location — 1000m geo-fence check skipped
                </span>
              )}
            </div>
          )}

          {/* Location */}
          <div className="tour-form-row">
            <div className="tour-form-group">
              <label className="tour-label">
                Your Location <span className="required">*</span>
              </label>
              <div className="location-input-group">
                <input type="text" name="visitLatitude"
                  className={`tour-input ${errors.visitLatitude ? 'error' : ''}`}
                  value={formData.visitLatitude} onChange={handleChange}
                  placeholder="Latitude" readOnly />
                <button type="button" className="btn-detect" onClick={detectLocation} disabled={detecting}>
                  {detecting ? '⏳' : '📍 Detect'}
                </button>
              </div>
              {errors.visitLatitude && <span className="tour-error-message">{errors.visitLatitude}</span>}
            </div>
            <div className="tour-form-group">
              <label className="tour-label">Longitude</label>
              <input type="text" name="visitLongitude" className="tour-input"
                value={formData.visitLongitude} onChange={handleChange}
                placeholder="Longitude" readOnly />
            </div>
          </div>

          {formData.visitLatitude && (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#e3f2fd',
              borderRadius: 8, fontSize: 12, color: '#1565c0' }}>
              ✅ Location: {formData.visitLatitude}, {formData.visitLongitude}
            </div>
          )}

          <div className="tour-form-row">
            <div className="tour-form-group tour-form-group full-width">
              <label className="tour-label">Remark</label>
              <textarea name="remark" className="tour-input"
                value={formData.remark} onChange={handleChange}
                placeholder="Optional remarks" rows={3}
                style={{ resize: 'vertical' }} />
            </div>
          </div>

          <div className="tour-form-actions">
            <button type="button" className="btn-tour-secondary"
              onClick={() => navigate('/tour/monthly')} disabled={isSubmitting}>
              Back
            </button>
            <button type="submit" className="btn-tour-primary"
              disabled={isSubmitting || detecting || !formData.visitLatitude}>
              {isSubmitting ? 'Logging...' : '✓ Log Visit'}
            </button>
          </div>
        </form>
      </div>

      {/* Today's visits */}
      <div style={{ marginTop: 28 }}>
        <h5 style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e', marginBottom: 14 }}>
          Today's Visits ({todayVisits.length})
        </h5>
        <div className="tour-table-container">
          {visitsLoading ? (
            <div className="tour-loading">Loading visits...</div>
          ) : (
            <table className="tour-table">
              <thead>
                <tr>
                  <th>#</th><th>Type</th><th>Name</th><th>Time</th><th>Location</th><th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {todayVisits.length > 0 ? todayVisits.map((visit, i) => (
                  <tr key={visit.id}>
                    <td style={{ fontWeight: 600, color: '#4a8c2a' }}>{i + 1}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: visit.subDealerId ? '#fff3e0' : '#e8f5e9',
                        color: visit.subDealerId ? '#e65100' : '#2e7d32',
                      }}>
                        {visit.subDealerId ? 'SUB-DEALER' : 'DEALER'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {visit.subDealer?.subDealerShopName || visit.dealer?.dealerShopName || '—'}
                      {visit.subDealerId && (
                        <div style={{ fontSize: 11, color: '#6c757d' }}>
                          Under: {visit.dealer?.dealerShopName}
                        </div>
                      )}
                    </td>
                    <td>{formatTime(visit.visitTime)}</td>
                    <td>
                      {visit.visitLatitude
                        ? <span style={{ fontSize: 12, color: '#4a8c2a' }}>
                            📍 {parseFloat(visit.visitLatitude).toFixed(4)},
                            {parseFloat(visit.visitLongitude).toFixed(4)}
                          </span>
                        : <span className="km-pending">—</span>}
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 13, color: '#495057' }}>
                      {visit.remark || <span className="km-pending">—</span>}
                    </td>
                  </tr>
                )) : (
                  <tr className="no-data-row">
                    <td colSpan="6">No visits logged today</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealerVisit;